import { Loading } from "@/components/ui";
import { BillingAlertStatus, GatewayType } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import { useBannerNotification } from "@/hooks/useBannerNotification";
import { useBillingStatus } from "@/hooks/useBillingStatus";
import { useCanCreateBillingPortal, usePermissionsV2Loaded } from "@/permissions/v2";
import { SubscriptionService } from "@/services/subscriptionService";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";

const BILLING_NOTIFICATION_ID = "billing-alert";

export const BillingAlertBanner = () => {
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const { billingStatus, isLoading } = useBillingStatus();
  const { firmContext } = useAuth();
  const { add, dismiss } = useBannerNotification();
  const isPermissionsLoaded = usePermissionsV2Loaded();
  const canManageBilling = useCanCreateBillingPortal(firmContext?.firmId);

  const v2Api = useAPI("v2");
  const subscriptionService = useMemo(() => new SubscriptionService(v2Api), [v2Api]);

  const isCritical = billingStatus?.status === BillingAlertStatus.CRITICAL;
  const isSuspended = billingStatus?.status === BillingAlertStatus.SUSPENDED;

  const handleManageSubscription = async () => {
    if (isLoadingPortal || !firmContext?.firmId) return;
    try {
      setIsLoadingPortal(true);
      const response = await subscriptionService.createCustomerPortalSession(
        firmContext.firmId,
        GatewayType.STRIPE
      );
      window.location.href = response.portal_url;
    } catch {
      toast.error("Failed to open subscription management portal.");
      setIsLoadingPortal(false);
    }
  };

  useEffect(() => {
    if (
      !isPermissionsLoaded ||
      isLoading ||
      !billingStatus ||
      billingStatus.status === BillingAlertStatus.CURRENT
    ) {
      dismiss(BILLING_NOTIFICATION_ID);
      return;
    }

    const type = isCritical || isSuspended ? "error" : "warning";
    const title = isSuspended
      ? "Your account has been paused due to pending invoices."
      : isCritical
        ? "Your subscription will be paused soon due to pending invoices."
        : "You have pending invoices.";
    const suffix = isSuspended ? "to resume service." : "to avoid service interruption.";
    const actionText = canManageBilling ? "resolve it" : "contact your admin";

    const message = (
      <span>
        {title} Please{" "}
        {canManageBilling ? (
          <span
            onClick={handleManageSubscription}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleManageSubscription();
            }}
            className={`cursor-pointer select-none font-bold underline transition-colors duration-200 ${
              type === "error" ? "hover:text-destructive-text" : "hover:text-warning-text"
            }`}
          >
            {actionText}
          </span>
        ) : (
          <span>{actionText}</span>
        )}{" "}
        {suffix}
      </span>
    );

    add({
      id: BILLING_NOTIFICATION_ID,
      type,
      message,
      dismissible: false,
    });
  }, [billingStatus, isLoading, isCritical, isSuspended, isPermissionsLoaded, canManageBilling]);

  return isLoadingPortal ? (
    <Loading overlay text="Opening subscription portal..." size={40} />
  ) : null;
};
